// MakeInIndiaCertificate.jsx
import React from "react";

export default function MakeInIndiaCertificate() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            Make in India Certificate
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            Make in India Certificate for Manufacturers
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks assists businesses in obtaining Make in India
            Certificates for government procurement preference and tender
            eligibility under Public Procurement (Preference to Make in India)
            Order.
          </p>
          <p className="text-gray-700">
            The certification process involves local content verification,
            self-declaration preparation, and third-party validation through QCI
            or authorized agencies for Class-I and Class-II local supplier status.
          </p>
        </div>

        {/* Right: Image (MAKE IN INDIA CERTIFICATE - Indian manufacturing, factory production, government procurement compliance) */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/247851/pexels-photo-247851.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Manufacturer obtaining Make in India Certificate"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks provides complete assistance for Make in India
              certification, helping manufacturers qualify as Class-I (50%+ local
              content) or Class-II local suppliers for government tenders.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Local content calculation and verification meeting DPIIT
                  threshold requirements for Class-I and Class-II classification.
                </li>
                <li>
                  Self-declaration preparation and CA/statutory auditor
                  certification for high-value government contracts.
                </li>
                <li>
                  Application support through Quality Council of India (QCI) or
                  authorized certification agencies with complete documentation.
                </li>
                <li>
                  Ongoing compliance advisory for certificate renewal and local
                  content maintenance across product portfolio.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for Make in India Certificate
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks assists in compiling all mandatory documents
              required for local content verification and MII certification.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Business registration proof (GST, PAN, Incorporation Certificate, Udyam/MSME).</li>
                <li>
                  Manufacturing details including factory license, layout plans,
                  and production process documentation.
                </li>
                <li>
                  Product information with specifications, bill of materials,
                  cost sheets, and local content percentage calculation.
                </li>
                <li>
                  Financial statements and CA-certified local content declaration
                  (required for contracts above ₹10 crores).
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm specializes in government procurement compliance with
              proven expertise in Make in India certification processes.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Government Scheme Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Complete understanding of Public Procurement Order, local
                  content norms, and Class-I/Class-II supplier classification rules.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Compliance Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Professional local content calculation, document structuring,
                  and certification agency coordination for tender eligibility.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Professional Service Delivery
                </h3>
                <p className="text-gray-700 text-sm">
                  End-to-end handling from eligibility assessment through
                  certificate issuance and GeM tender compliance support.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Secure Government Tender Preference
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to obtain your Make in India
                Certificate and gain Class-I local supplier preference in
                government procurement and GeM tenders nationwide.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
